import { Temporal } from '@js-temporal/polyfill';
import { JourneyPlannerItineraryTransportMode, Leg } from '@ridango/components';
import { LegModeType, TRAVEL_PLANNER_WCAG_TRANSLATION_KEYS } from './itinerary-legs-overview.const';
import {
    BuildLegAriaLabelParams,
    BuildTransitLegAriaLabelParams,
    BuildWaitLegAriaLabelParams,
    BuildWalkLegAriaLabelParams,
    ResolveLegEndpointLabelParams
} from './itinerary-legs-overview.interface';

export class ItineraryLegsOverviewUtils {
    private static TRANSLATION_KEYS = TRAVEL_PLANNER_WCAG_TRANSLATION_KEYS;
    /**
     * Builds the full screen-reader label for one itinerary leg.
     * Handles walk, wait and transit legs, adds step numbering, and appends
     * service alerts when available.
     */
    static buildLegAriaLabel(params: BuildLegAriaLabelParams): string {
        const {
            legN,
            legMode,
            legData,
            originalLeg,
            nextLeg,
            totalLegs,
            originUseLocationFromMap,
            destinationUseLocationFromMap,
            screenReaderEnabled,
            includeIntermediateStopsInAria,
            timezone,
            legModeMap,
            translate,
            formatDate,
            formatMoney,
            formatDuration,
            formatDistance,
            locale
        } = params;
        const mode = legMode ?? legData?.mode;
        const leg = legData;
        const startTime = this.formatTime(leg?.startTime, timezone, formatDate);
        const endTime = this.formatTime(leg?.endTime, timezone, formatDate);

        if (!leg) {
            return '';
        }

        const phrases: string[] = [];
        phrases.push(translate(this.TRANSLATION_KEYS.STEP, { step: legN }));

        // Branch by leg type and delegate phrase construction to focused helpers.
        if (mode === legModeMap.Walk) {
            return this.buildWalkLegAriaLabel({
                phrases,
                leg,
                legN,
                totalLegs,
                startTime,
                endTime,
                originUseLocationFromMap,
                destinationUseLocationFromMap,
                translate,
                formatDuration,
                formatDistance,
                locale
            });
        }

        if (mode === legModeMap.Wait) {
            return this.buildWaitLegAriaLabel({
                phrases,
                leg,
                nextLeg,
                translate,
                formatDuration
            });
        }

        return this.buildTransitLegAriaLabel({
            phrases,
            leg,
            mode,
            legModeMap,
            legN,
            totalLegs,
            startTime,
            endTime,
            originalLeg,
            timezone,
            formatDate,
            formatMoney,
            originUseLocationFromMap,
            destinationUseLocationFromMap,
            screenReaderEnabled,
            includeIntermediateStopsInAria,
            translate
        });
    }

    /**
     * Builds aria-label content for a walking leg.
     * Handles first-leg start time and last-leg final arrival phrases.
     */
    private static buildWalkLegAriaLabel(params: BuildWalkLegAriaLabelParams): string {
        const {
            phrases,
            leg,
            legN,
            totalLegs,
            startTime,
            endTime,
            originUseLocationFromMap,
            destinationUseLocationFromMap,
            translate,
            formatDuration,
            formatDistance,
            locale
        } = params;
        const walkDuration = this.formatDurationValue(leg.duration, formatDuration);
        const walkDistance = this.formatDistanceValue(leg.distance, formatDistance, locale);
        // Endpoint labels can be replaced with LOCATION_FROM_MAP for map-selected origin/destination.
        const from = this.resolveLegEndpointLabel({
            rawLabel: this.formatStopWithPlatform(leg.from, translate),
            isOriginEndpoint: legN === 1,
            isDestinationEndpoint: false,
            originUseLocationFromMap,
            destinationUseLocationFromMap,
            translate
        });
        const to = this.resolveLegEndpointLabel({
            rawLabel: this.formatStopWithPlatform(leg.to, translate),
            isOriginEndpoint: false,
            isDestinationEndpoint: legN === totalLegs,
            originUseLocationFromMap,
            destinationUseLocationFromMap,
            translate
        });

        if (legN === 1) {
            phrases.push(translate(this.TRANSLATION_KEYS.START_TIME, { time: startTime }));
        }

        if (legN === totalLegs) {
            phrases.push(
                translate(this.TRANSLATION_KEYS.WALK_TO_DESTINATION, {
                    duration: walkDuration,
                    distance: walkDistance,
                    destination: to
                })
            );
        } else {
            phrases.push(
                translate(this.TRANSLATION_KEYS.WALK_FROM_TO, {
                    duration: walkDuration,
                    distance: walkDistance,
                    from,
                    to
                })
            );
        }

        if (legN === totalLegs) {
            phrases.push(translate(this.TRANSLATION_KEYS.FINAL_ARRIVAL_TIME, { time: endTime }));
        }

        return phrases.join(' ');
    }

    /**
     * Builds aria-label content for a waiting leg,
     * including transfer platform when it can be resolved.
     */
    private static buildWaitLegAriaLabel(params: BuildWaitLegAriaLabelParams): string {
        const { phrases, leg, nextLeg, translate, formatDuration } = params;
        const waitDuration = this.formatDurationValue(leg.duration, formatDuration);
        // Wait legs usually sit between transit legs, so use the next leg platform when available.
        const transferPlatform = nextLeg?.from?.platformCode || leg?.to?.platformCode || leg?.from?.platformCode;

        if (transferPlatform) {
            phrases.push(translate(this.TRANSLATION_KEYS.TRANSFER_TO_PLATFORM, { platform: transferPlatform }));
        }

        phrases.push(translate(this.TRANSLATION_KEYS.WAIT_FOR_NEXT_DEPARTURE, { duration: waitDuration }));
        return phrases.join(' ');
    }

    /**
     * Builds aria-label content for a transit leg:
     * departure info, boarding details, stops, fare, arrival and disruption text.
     */
    private static buildTransitLegAriaLabel(params: BuildTransitLegAriaLabelParams): string {
        const {
            phrases,
            leg,
            mode,
            legModeMap,
            legN,
            totalLegs,
            startTime,
            endTime,
            originalLeg,
            timezone,
            formatDate,
            formatMoney,
            originUseLocationFromMap,
            destinationUseLocationFromMap,
            screenReaderEnabled,
            includeIntermediateStopsInAria,
            translate
        } = params;
        // Compose transit leg description from reusable pieces: departure, boarding, stops, fare, arrival, alerts.
        const modeLabel = this.getModeLabel(mode, legModeMap, translate);
        const routeName = leg.routeShortName || leg.route || '';
        const operator = leg.operatorName || leg.agencyName;
        const destination = leg.headsign || leg.routeLongName;
        const from = this.resolveLegEndpointLabel({
            rawLabel: this.formatStopWithPlatform(leg.from, translate),
            isOriginEndpoint: legN === 1,
            isDestinationEndpoint: false,
            originUseLocationFromMap,
            destinationUseLocationFromMap,
            translate
        });
        const to = this.resolveLegEndpointLabel({
            rawLabel: this.formatStopWithPlatform(leg.to, translate),
            isOriginEndpoint: false,
            isDestinationEndpoint: legN === totalLegs,
            originUseLocationFromMap,
            destinationUseLocationFromMap,
            translate
        });
        const stopCount = leg.intermediateStops?.length || 0;

        if (startTime) {
            const originalStartTime = this.formatTime(originalLeg?.startTime, timezone, formatDate);
            // If real-time shifted departure differs from original schedule, include both timestamps.
            if (originalStartTime && originalStartTime !== startTime) {
                phrases.push(
                    translate(this.TRANSLATION_KEYS.DEPARTURE_WITH_ORIGINAL, {
                        time: startTime,
                        originalTime: originalStartTime
                    })
                );
            } else {
                phrases.push(translate(this.TRANSLATION_KEYS.DEPARTURE, { time: startTime }));
            }
        }

        let boardText = translate(this.TRANSLATION_KEYS.BOARD, { mode: modeLabel });
        if (routeName) {
            boardText += ` ${translate(this.TRANSLATION_KEYS.BOARD_ROUTE, { route: routeName })}`;
        }
        if (operator) {
            boardText += `, ${translate(this.TRANSLATION_KEYS.BOARD_OPERATED_BY, { operator })}`;
        }
        if (destination) {
            boardText += `, ${translate(this.TRANSLATION_KEYS.BOARD_TOWARDS, { destination })}`;
        }
        if (from) {
            boardText += ` ${translate(this.TRANSLATION_KEYS.BOARD_AT, { stop: from })}`;
        }
        phrases.push(`${boardText}.`);

        if (stopCount > 0) {
            phrases.push(
                translate(
                    stopCount === 1
                        ? this.TRANSLATION_KEYS.TRAVEL_STOP_SINGULAR
                        : this.TRANSLATION_KEYS.TRAVEL_STOP_PLURAL,
                    {
                        count: stopCount
                    }
                )
            );
        }

        if (leg.price != null) {
            phrases.push(
                translate(this.TRANSLATION_KEYS.PRICE, {
                    price: this.formatPrice(leg.price, leg.currency, formatMoney)
                })
            );
        }

        if (stopCount > 0 && includeIntermediateStopsInAria) {
            const stops = this.buildIntermediateStopsAriaText(leg, timezone, formatDate, translate);
            if (stops) {
                phrases.push(translate(this.TRANSLATION_KEYS.STOPS_LIST, { stops }));
            }
            if (screenReaderEnabled) {
                phrases.push(translate(this.TRANSLATION_KEYS.DOUBLE_TAP_HIDE_STOPS));
            }
        } else if (screenReaderEnabled && stopCount > 0) {
            phrases.push(translate(this.TRANSLATION_KEYS.DOUBLE_TAP_SHOW_STOPS, { count: stopCount }));
        }

        phrases.push(translate(this.TRANSLATION_KEYS.ARRIVE_AT, { stop: to, time: endTime }));

        const serviceAlertText = this.resolveServiceAlertText(leg);
        if (serviceAlertText) {
            phrases.push(translate(this.TRANSLATION_KEYS.SERVICE_ALERT, { message: serviceAlertText }));
        }

        return phrases.join(' ');
    }

    /**
     * Formats stop label and appends translated platform suffix when present.
     */
    private static formatStopWithPlatform(stop: Leg['from'], translate: BuildLegAriaLabelParams['translate']): string {
        if (!stop?.name) {
            return '';
        }

        const platform = stop.platformCode
            ? translate(this.TRANSLATION_KEYS.PLATFORM, { platform: stop.platformCode })
            : '';

        return [stop.name, platform].filter(Boolean).join(', ');
    }

    /**
     * Formats timestamp to HH:mm using provided date formatter callback.
     */
    private static formatTime(
        timestamp: number,
        timezone?: string,
        formatDate?: (value: Date | string | number, format?: string, timezone?: string) => string
    ): string {
        if (!timestamp) {
            return '';
        }

        return formatDate(timestamp, 'HH:mm', timezone) || '';
    }

    /**
     * Formats duration using the provided formatter (pipe-compatible) when available.
     * Falls back to minute-based formatting.
     */
    private static formatDurationValue(
        duration: string | number | Temporal.Duration,
        formatDuration?: (value: string | number | Temporal.Duration) => string
    ): string {
        if (formatDuration) {
            return formatDuration(duration) || '';
        }

        const minutes = Math.max(1, Math.round(this.resolveDurationMinutes(duration)));
        return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
    }

    /**
     * Resolves duration value to minutes from supported input types.
     * Supports numeric values and Temporal.Duration.
     */
    private static resolveDurationMinutes(duration: string | number | Temporal.Duration): number {
        if (typeof duration === 'number' && Number.isFinite(duration)) {
            return (duration || 0) / 60;
        }

        if (duration && typeof duration === 'object' && 'total' in duration && typeof duration.total === 'function') {
            const totalMinutes = duration.total({ unit: 'minutes' });
            return Number.isFinite(totalMinutes) ? totalMinutes : 0;
        }

        return 0;
    }

    /**
     * Formats distance via provided formatter (pipe-compatible) when available.
     * Falls back to rounded meters with locale-aware number formatting.
     */
    private static formatDistanceValue(
        distance: number,
        formatDistance?: (value: number) => string,
        locale = 'en-US'
    ): string {
        if (formatDistance) {
            return formatDistance(distance) || '';
        }

        const roundedDistance = Math.max(0, Math.round(distance || 0));
        return `${new Intl.NumberFormat(locale).format(roundedDistance)} m`;
    }

    /**
     * Formats monetary value with the provided money formatter and appends currency code
     * when currency is present.
     */
    private static formatPrice(
        price: number | string | { amount: number },
        currency: string | undefined,
        formatMoney?: (value: number | string | { amount: number }, multiplier?: number) => string
    ): string {
        const formattedMoney = formatMoney(price);

        return currency ? `${formattedMoney}${currency}` : formattedMoney;
    }

    /**
     * Resolves translated transport mode label used in boarding sentence.
     */
    private static getModeLabel(
        mode: LegModeType,
        legModeMap: typeof JourneyPlannerItineraryTransportMode,
        translate: BuildLegAriaLabelParams['translate']
    ): string {
        switch (mode) {
            case legModeMap.Bus:
                return translate(this.TRANSLATION_KEYS.MODE_BUS);
            case legModeMap.Tram:
                return translate(this.TRANSLATION_KEYS.MODE_TRAM);
            case legModeMap.Rail:
                return translate(this.TRANSLATION_KEYS.MODE_TRAIN);
            case legModeMap.Ferry:
                return translate(this.TRANSLATION_KEYS.MODE_FERRY);
            case legModeMap.Trolleybus:
                return translate(this.TRANSLATION_KEYS.MODE_TROLLEYBUS);
            case legModeMap.Flexible:
                return translate(this.TRANSLATION_KEYS.MODE_FLEXIBLE);
            default:
                return translate(this.TRANSLATION_KEYS.MODE_TRANSPORT);
        }
    }

    /**
     * Extracts the first available disruption message text for aria output.
     * Only announces the header when a description is also present — header-only messages have no visible UI text.
     */
    private static resolveServiceAlertText(leg: Leg): string | null {
        const firstMessage = leg.disruptionMessages?.[0];
        if (!firstMessage?.descriptionTextDefault) {
            return null;
        }

        return firstMessage.headerTextDefault || null;
    }

    /**
     * Builds a compact intermediate stops string for screen-reader-only expanded aria labels.
     */
    private static buildIntermediateStopsAriaText(
        leg: Leg,
        timezone: string | undefined,
        formatDate: BuildLegAriaLabelParams['formatDate'] | undefined,
        translate: BuildLegAriaLabelParams['translate']
    ): string {
        if (!leg?.intermediateStops?.length) {
            return '';
        }

        return leg.intermediateStops
            .map((stop) => {
                const time = stop?.departure ? this.formatTime(stop.departure, timezone, formatDate) : '';
                const name = this.formatStopWithPlatform(stop, translate);
                return [time, name].filter(Boolean).join(' ');
            })
            .filter(Boolean)
            .join(', ');
    }

    /**
     * Replaces origin/destination endpoint labels with map-location label when user selected point from map.
     */
    private static resolveLegEndpointLabel(params: ResolveLegEndpointLabelParams): string {
        const {
            rawLabel,
            isOriginEndpoint,
            isDestinationEndpoint,
            originUseLocationFromMap,
            destinationUseLocationFromMap,
            translate
        } = params;

        // For map-selected endpoints, speak a stable label instead of raw coordinates.
        const isEndpoint = isOriginEndpoint || isDestinationEndpoint;
        const isCoordinateLabel = this.looksLikeUserDefinedCoordinate(rawLabel);
        if (
            (isOriginEndpoint && originUseLocationFromMap) ||
            (isDestinationEndpoint && destinationUseLocationFromMap) ||
            (isEndpoint && isCoordinateLabel)
        ) {
            return translate('LOCATION_FROM_MAP');
        }

        return rawLabel;
    }

    /**
     * Detects OTP-style coordinate labels like:
     * "From user defined coordinate: #6589095#1369722".
     */
    private static looksLikeUserDefinedCoordinate(value: string): boolean {
        if (!value) {
            return false;
        }

        const normalized = value.toLowerCase();
        return normalized.includes('user defined coordinate') || /#-?\d+(?:[.,]\d+)?#-?\d+(?:[.,]\d+)?/.test(value);
    }
}
